import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { Eye, EyeOff, ChevronDown } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_opport-biz-ma/artifacts/oknd9jsa_Logo%20Club%20Business%20Maroc%20Annuaire.png";
const APPLY_URL = "https://tally.so/r/EkBlVB";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [devOpen, setDevOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (e, p) => { setEmail(e); setPassword(p); };

  // Afficher le bloc Mode développement uniquement en dev/preview, jamais en production
  const isDevEnv = typeof window !== "undefined" && (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.endsWith(".preview.emergentagent.com")
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #081026 0%, #0D1B3E 50%, #081530 100%)" }}
    >
      {/* Decorative glow */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: "600px", height: "600px",
          background: "radial-gradient(circle, rgba(201,162,39,0.06) 0%, transparent 70%)",
        }}
      />
      {/* Moroccan pattern dots */}
      <div className="absolute inset-0 pointer-events-none opacity-5" style={{
        backgroundImage: "radial-gradient(circle, #C9A227 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />

      {/* Login card */}
      <div className="relative z-10 w-full max-w-sm mx-4 animate-fade-in-up">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src={LOGO_URL} alt="Club Business Maroc" className="w-28 h-28 object-contain rounded-xl" />
          <div className="mt-5 text-center">
            <h1
              className="font-outfit font-bold text-white text-2xl"
              style={{ letterSpacing: "0.08em" }}
            >
              CLUB BUSINESS
            </h1>
            <p
              className="font-outfit font-semibold text-gold text-lg mt-1"
              style={{ letterSpacing: "0.35em", paddingLeft: "0.35em" }}
            >
              MAROC
            </p>
            <p
              className="text-xs mt-4"
              style={{ color: "rgba(255,255,255,0.6)", letterSpacing: "0.05em" }}
            >
              Accès sur invitation
            </p>
          </div>
        </div>

        <div className="cbm-glass rounded-2xl p-8 shadow-card">
          <h1 className="font-outfit text-2xl font-semibold text-white mb-1 text-center">Connexion</h1>
          <p className="text-cbm-muted text-sm text-center mb-6">Accès réservé aux membres</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="cbm-label block mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                className="cbm-input"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Mot de passe</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  className="cbm-input pr-10"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cbm-muted hover:text-cbm-secondary transition-colors"
                  onClick={() => setShowPwd(!showPwd)}
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-red-400 text-sm bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-2.5">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="cbm-btn-primary w-full justify-center text-sm py-2.5"
            >
              {loading ? (
                <><span className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />Connexion...</>
              ) : "Se connecter"}
            </button>
          </form>

          {/* Forgot password */}
          <div className="mt-3 text-center">
            <a href="/forgot-password" className="text-cbm-muted hover:text-gold text-xs transition-colors" data-testid="forgot-password-link">
              Mot de passe oublié ?
            </a>
          </div>

          {/* Apply / become member */}
          <div className="mt-5 pt-4 border-t cbm-divider text-center">
            <p className="text-xs mb-1.5" style={{ color: "rgba(255,255,255,0.5)" }}>
              Pas encore membre ?
            </p>
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="apply-link"
              className="inline-flex items-center gap-1.5 text-xs font-medium transition-all px-3 py-1.5 rounded-md border border-white/10 hover:border-gold/40 hover:bg-white/5"
              style={{ color: "rgba(255,255,255,0.75)" }}
            >
              Postuler pour rejoindre
              <span aria-hidden style={{ fontSize: 10 }}>→</span>
            </a>
          </div>

          {/* Conditions & Confidentialité */}
          <div className="mt-4 text-center">
            <a
              href="/conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cbm-muted hover:text-gold text-xs transition-colors"
              data-testid="legal-link"
            >
              Conditions & confidentialité
            </a>
          </div>

          {/* Dev mode — visible uniquement en dev/preview */}
          {isDevEnv && (
            <div className="mt-6 border-t cbm-divider pt-4">
            <button
              data-testid="dev-mode-toggle"
              className="flex items-center justify-between w-full text-cbm-muted hover:text-cbm-secondary text-xs transition-colors"
              onClick={() => setDevOpen(!devOpen)}
            >
              <span className="uppercase tracking-widest font-semibold">Mode développement</span>
              <ChevronDown size={14} className={`transition-transform ${devOpen ? "rotate-180" : ""}`} />
            </button>
            {devOpen && (
              <div className="mt-3 space-y-2 animate-fade-in">
                <p className="text-cbm-muted text-xs mb-2">Comptes de test — cliquez pour remplir :</p>
                <button
                  data-testid="dev-login-admin"
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 transition-colors border border-gold/10 hover:border-gold/20"
                  onClick={() => quickLogin("admin@clubbusiness.ma", "Admin@2024!")}
                >
                  <span className="cbm-badge cbm-badge-featured text-xs mr-2">ADMIN</span>
                  <span className="text-cbm-secondary text-xs">admin@clubbusiness.ma</span>
                </button>
                <button
                  data-testid="dev-login-member"
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 transition-colors border border-white/5 hover:border-white/10"
                  onClick={() => quickLogin("membre@clubbusiness.ma", "Membre@2024!")}
                >
                  <span className="cbm-badge cbm-badge-verified text-xs mr-2">MEMBRE</span>
                  <span className="text-cbm-secondary text-xs">membre@clubbusiness.ma</span>
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
